import { makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles/createTheme";

// Aspen-Birch rebrand: card-based sign-in matching the brand mock-up — centered
// white card (logo, title, subtitle, labeled fields with icons, red CTA, secure-
// access divider, help line) on the grey page background, with a footer below.
export const useStyles = makeStyles((theme: Theme) => ({
    loginContainer: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.palette.backgrounds.main,
        // Fill the viewport below the 72px login navbar WITHOUT creating a page
        // scrollbar: border-box so the padding counts inside the min-height (the
        // login routes render no CssBaseline, so content-box is the default here).
        boxSizing: "border-box",
        minHeight: "calc(100vh - 72px)",
        padding: theme.spacing(4),
    },
    loginCard: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "#FFFFFF",
        borderRadius: 24,
        boxShadow: "0 18px 44px rgba(6,35,63,0.08)",
        padding: `${theme.spacing(6)} ${theme.spacing(12)}`,
        width: "100%",
        maxWidth: 760,
    },
    loginLogo: {
        marginBottom: theme.spacing(4),
        "& img": {
            width: 280,
            maxWidth: "100%",
            display: "block",
        },
    },
    loginTitle: {
        color: `${theme.palette.text.primary} !important`,
        fontSize: "26px !important",
        fontWeight: "700 !important",
        textAlign: "center",
    },
    loginSubtitle: {
        color: `${theme.palette.text.secondary} !important`,
        fontSize: "16px !important",
        fontWeight: "400 !important",
        textAlign: "center",
        marginTop: `${theme.spacing(1)} !important`,
        marginBottom: `${theme.spacing(4)} !important`,
    },
    loginForm: {
        width: "100%",
        maxWidth: 640,
    },
    fieldsContainer: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(4),
    },
    fieldBlock: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
    },
    fieldLabelRow: {
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
    },
    fieldLabel: {
        color: `${theme.palette.text.primary} !important`,
        fontSize: "16px !important",
        fontWeight: "700 !important",
    },
    forgotPasswordLink: {
        textDecoration: "none",
        "& .MuiTypography-root": {
            color: `${theme.palette.info.main} !important`,
            fontSize: "15px !important",
            fontWeight: "600 !important",
        },
        "&:hover .MuiTypography-root": {
            textDecoration: "underline",
        },
    },
    textField: {
        width: "100%",
        "& .MuiOutlinedInput-root": {
            fontFamily: "Source Sans 3 !important",
            fontSize: "16px !important",
            fontWeight: "400 !important",
            backgroundColor: "#FFFFFF",
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.info.main,
            },
        },
        "& .MuiInputAdornment-root .MuiSvgIcon-root": {
            color: theme.palette.text.secondary,
            fontSize: 20,
        },
    },
    errorMessage: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(6),
        textAlign: "center",
    },
    submitButton: {
        "&.MuiButtonBase-root": {
            background: theme.palette.info.light,
            color: "#FFFFFF",
            height: "48px !important",
            width: "100%",
            borderRadius: 10,
            fontFamily: "Source Sans 3",
            fontSize: "17px",
            fontWeight: 700,
            marginTop: theme.spacing(6),
            "&:hover": {
                background: theme.palette.info.main,
            },
        },
    },
    // "— [lock] Secure corporate access —" divider row.
    secureRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(3),
        marginTop: theme.spacing(5),
        width: "100%",
        "&::before, &::after": {
            content: '""',
            flex: 1,
            borderTop: `1px solid ${theme.palette.border.select}`,
        },
    },
    secureRowInner: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        color: theme.palette.text.primary,
        "& .MuiSvgIcon-root": {
            fontSize: 18,
        },
    },
    secureRowText: {
        fontSize: "15px !important",
        fontWeight: "400 !important",
        color: `${theme.palette.text.primary} !important`,
    },
    helpRow: {
        marginTop: theme.spacing(4),
        textAlign: "center",
        fontFamily: "Source Sans 3",
        fontSize: 16,
        color: theme.palette.text.secondary,
    },
    helpRowAction: {
        color: theme.palette.info.main,
        fontWeight: 600,
        marginLeft: 4,
        textDecoration: "none",
        "&:hover": {
            textDecoration: "underline",
        },
    },
    // Footer under the card: © year MADERA. All rights reserved.
    footer: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(4),
        marginTop: theme.spacing(4),
        fontFamily: "Source Sans 3",
        fontSize: 15,
        color: theme.palette.text.secondary,
    },
}));
